import { Currency, PayoutStatus, PayoutType } from "@daytrip/legacy-enums";
import { Expose, Type } from "class-transformer";
import { IsArray, IsDate, IsDefined, IsInt, IsString, IsUUID, ValidateIf, IsNumber, IsEnum } from "class-validator";
import { v4 as uuid } from "uuid";

import { CDBaseEntity, CDColumn, CDCreateDateColumn, CDEntity, CDObjectIdColumn } from "../orm";
import { statusGetter } from "../statusGetter";
import { TransformToDate } from "../transformers/transformToDate";

import { DateRange } from "./DateRange";

const AVAILABLE_GATEWAY_ID_PAYOUT_TYPES = [PayoutType.Mangopay, PayoutType.Hyperwallet];

@CDEntity("payouts")
export class Payout extends CDBaseEntity {
    @CDObjectIdColumn()
    @IsUUID("4")
    @Expose()
    public _id: string = uuid();

    @CDCreateDateColumn()
    @IsDate()
    @IsDefined()
    @Expose()
    @TransformToDate
    public createdAt: Date = new Date();

    @CDColumn()
    @Expose()
    @Type(() => DateRange)
    public period?: DateRange;

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public userId: string;

    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public amount: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public customAmount: number = 0;

    @CDColumn()
    @IsString()
    @IsDefined()
    @ValidateIf((payout: Payout) => payout.customAmount !== 0)
    @Expose()
    public customDescription?: string;

    @CDColumn()
    @IsEnum(Currency)
    @IsDefined()
    @Expose()
    public currency = Currency.Euro;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public type: PayoutType;

    @CDColumn()
    @IsString()
    @IsDefined()
    @ValidateIf((p: Payout) => p.type === PayoutType.Other)
    @Expose()
    public description?: string;

    @CDColumn()
    // only Mangopay can have transfer ids since we are paying out with mangopay (thru the system)
    @IsArray()
    @Expose()
    public transferGatewayIds?: string[];

    @CDColumn()
    // only Mangopay and Hyperwallet can have gateway id
    @IsString()
    @IsDefined()
    @ValidateIf(
        (p: Payout) => AVAILABLE_GATEWAY_ID_PAYOUT_TYPES.includes(p.type) && p.status === PayoutStatus.Fulfilled,
    )
    @Expose()
    public payoutGatewayId?: string;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public fulfilledAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public failedAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public unknownAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public processingAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public approvedAt?: Date;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public approvedByUserId?: string;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public declinedAt?: Date;

    @IsDate()
    @TransformToDate
    @Expose()
    public latestWebhookNotificationDate?: Date;

    public get status(): PayoutStatus {
        return statusGetter(this, Payout, PayoutStatus);
    }

    public static statusPrototype(status: PayoutStatus): Payout {
        const defined = new Date();

        switch (status) {
            case PayoutStatus.Failed:
                return {
                    failedAt: defined,
                    fulfilledAt: undefined,
                } as Payout;

            case PayoutStatus.Fulfilled:
                return {
                    failedAt: undefined,
                    fulfilledAt: defined,
                } as Payout;

            case PayoutStatus.Unknown:
                return {
                    failedAt: undefined,
                    fulfilledAt: undefined,
                    unknownAt: defined,
                } as Payout;

            case PayoutStatus.Approved:
                return {
                    failedAt: undefined,
                    fulfilledAt: undefined,
                    declinedAt: undefined,
                    processingAt: undefined,
                    approvedAt: defined,
                } as Payout;

            case PayoutStatus.Declined:
                return {
                    failedAt: undefined,
                    fulfilledAt: undefined,
                    declinedAt: defined,
                } as Payout;

            case PayoutStatus.Processing:
                return {
                    fulfilledAt: undefined,
                    failedAt: undefined,
                    approvedAt: defined,
                    processingAt: defined,
                } as Payout;

            case PayoutStatus.Pending:
            default:
                return {
                    failedAt: undefined,
                    fulfilledAt: undefined,
                    unknownAt: undefined,
                    approvedAt: undefined,
                    declinedAt: undefined,
                } as Payout;
        }
    }
}
